import AccessDeniedPage from "@/app/(other-pages)/access-denied/page";
import { categories } from "@/data/product-categories";
import { LANDING_PAGE_URL } from "config";
import { set } from "lodash";
import { list } from "postcss";

const routes = {
    eCommerce: {
        dashboard: "/ecommerce",
        products: "/ecommerce/products",
        createProduct: "/ecommerce/products/create",
        productDetails: (slug: string) => `/ecommerce/products/${slug}`,
        ediProduct: (slug: string) => `/ecommerce/products/${slug}/edit`,
        categories: "/ecommerce/categories",
        createCategory: "/ecommerce/categories/create",
        editCategory: (id: string) => `/ecommerce/categories/${id}/edit`,
        orders: "/ecommerce/orders",
        createOrder: "/ecommerce/orders/create",
        orderDetails: (id: string) => `/ecommerce/orders/${id}`,
        editOrder: (id: string) => `/ecommerce/orders/${id}/edit`,
        reviews: "/ecommerce/reviews",
        shop: "/ecommerce/shop",
        cart: "/ecommerce/cart",
        checkout: "/ecommerce/checkout",
        trackingId: (id: string) => `/ecommerce/tracking/${id}`,
    },
    searchAndFilter: {
        realEstate: "/search/real-estate",
        nft: "/search/nft",
        flight: "/search/flight",
    },

    support: {
        dashboard: "/support",
        inbox: "/support/inbox",
        supportCategory: (category: string) => `/support/inbox/${category}`,
        messageDetails: (id: string) => `/support/inbox/${id}`,
        snippets: "/support/snippets",
        createSnippet: "/support/snippets/create",
        viewSnippet: (id: string) => `/support/snippets/${id}`,
        editSnippet: (id: string) => `/support/snippets/${id}/edit`,
        templates: "/support/templates",
        createTemplate: "/support/templates/create",
        viewTemplate: (id: string) => `/support/templates/${id}`,
        editTemplate: (id: string) => `/support/templates/${id}/edit`,
    },
    orders: {
        dashboard: "/orders",
        list: "/orders/list",
        customerProfile: "/orders/customer-profile",
        createAgentOrder: "/orders/agent/create",
        createOrder: "/orders/create",
        report: "/orders/report",
        editAgentOrder: (id: string) => `/orders/agent/${id}/edit`,
        editOrder: (id: string) => `/orders/${id}/edit`,
        orderDetails: (id: string) => `/orders/${id}`,
        billingDetails: (id: string) => `/orders/billing/${id}`,
        tracking: (id: string) => `/orders/tracking/${id}`,
    },
    categories: {
        list: "/categories/list",
    },
    clients: {
        list: "/clients/list",
        create: "/clients/create",
        details: (id: string) => `/clients/${id}`,
    },
    payments: {
        list: "/payments/list",
    },
    shipments: {
        dashboard: "/shipments",
        list: "/shipments/list",
        customerProfile: "/shipments/customer-profile",
        createShipment: "/shipments/create",
        editShipment: (id: string) => `/shipments/${id}/edit`,
        shipmentDetails: (id: string) => `/shipments/${id}`,
    },
    appointment: {
        dashboard: "/appointment",
        appointmentList: "/appointment/list",
    },
    crm: {
        dashboard: "/crm",
    },
    affiliate: {
        dashboard: "https://isomorphic-dnd.vercel.app",
    },
    executive: {
        dashboard: "/executive",
    },
    project: {
        dashboard: "/project",
    },
    socialMedia: {
        dashboard: "/social-media",
    },
    jobBoard: {
        dashboard: "/job-board",
        jobFeed: "/job-board/feed",
    },
    analytics: "/analytics",
    financial: {
        dashboard: "/financial",
    },
    file: {
        dashboard: "/file",
        manager: "/file-manager",
        upload: "/file-manager/upload",
        create: "/file-manager/create",
    },
    pos: {
        index: "/point-of-sale",
    },
    eventCalendar: "/event-calendar",
    userManagement: {
        dashboard: "/user-management",
        roles: "/user-management/roles",
        createRole: "/user-management/roles/create",
        editRole: (id: string) => `/user-management/roles/${id}/edit`,
        users: "/user-management/users",
        createUser: "/user-management/users/create",
        editUser: (id: string) => `/user-management/users/${id}/edit`,
    },
    settings: {
        general: {
            list: "/settings/general",
            create: "/settings/general/create",
            edit: (id: string) => `/settings/general/${id}/edit`,
        },
        services: {
            list: "/settings/services",
            create: "/settings/services/create",
            edit: (id: string) => `/settings/services/${id}/edit`,
            delete: (id: string) => `/settings/services/${id}/delete`,
        },
        partners: {
            list: "/settings/partners",
            create: "/settings/partners/create",
            edit: (id: string) => `/settings/partners/${id}/edit`,
            delete: (id: string) => `/settings/partners/${id}/delete`,
        },
        notifications: "/settings/notifications",
    },
    billing: {
        home: "/billing",
        create: "/billing/create",
        details: (id: string) => `/billing/${id}`,
        edit: (id: string) => `/billing/${id}/edit`,
        builder: "/billing/builder",
    },
    landing : LANDING_PAGE_URL,
    imageViewer: "/image-viewer",
    widgets: {
        cards: "/widgets/cards",
        icons: "/widgets/icons",
        charts: "/widgets/charts",
        maps: "/widgets/maps",
        banners: "/widgets/banners",
    },
    tables: {
        basic: "/tables/basic",
        collapsible: "/tables/collapsible",
        enhanced: "/tables/enhanced",
        pagination: "/tables/pagination",
        search: "/tables/search",
        stickyHeader: "/tables/sticky-header",
        resizable: "/tables/resizable",
        pinning: "/tables/pinning",
        dnd: "/tables/dnd",
    },
    multiStep: "/multi-step",
    forms: {
        profileSettings: "/forms/profile-settings",
        notificationPreference: "/forms/profile-settings/notification",
        personalInformation: "/forms/profile-settings/profile",
        newsletter: "/forms/newsletter",
    },
    emailTemplates: "/email-templates",
    profile: "/profile",
    welcome: "/welcome",
    comingSoon: "/coming-soon",
    accessDenied: "/access-denied",
    notFound: "/not-found",
    maintenance: "/maintenance",
    blank: "/blank",
    auth: {
        signUp1: "/auth/sign-up-1",
        signUp2: "/auth/sign-up-2",
        signUp3: "/auth/sign-up-3",
        signUp4: "/auth/sign-up-4",
        signUp5: "/auth/sign-up-5",
        // sign in
        signIn1: "/auth/sign-in-1",
        signIn2: "/auth/sign-in-2",
        signIn3: "/auth/sign-in-3",
        signIn4: "",
        signIn5: "/auth/sign-in-5",
        // forgot password
        forgotPassword1: "/auth/forgot-password-1",
        forgotPassword2: "/auth/forgot-password-2",
        forgotPassword3: "/auth/forgot-password-3",
        forgotPassword4: "/auth/forgot-password-4",
        forgotPassword5: "/auth/forgot-password-5",
        // OTP
        otp1: "/auth/otp-1",
        otp2: "/auth/otp-2",
        otp3: "/auth/otp-3",
        otp4: "/auth/otp-4",
        otp5: "/auth/otp-5",
    },
    signIn: "/signin",
};

export default routes;
